import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle, Info, X, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export type ToastVariant = "info" | "success" | "warning" | "error";

export interface ToastProps {
  variant?: ToastVariant;
  title?: string;
  message: string;
  icon?: React.ReactNode;
  duration?: number; // in milliseconds
  onDismiss?: () => void;
  className?: string;
  /**
   * Whether to use glassmorphism effect
   */
  glassmorphic?: boolean;
}

const Toast: React.FC<ToastProps> = ({
  variant = "info",
  title,
  message,
  icon,
  duration = 5000, // Default 5 seconds
  onDismiss,
  className = "",
  glassmorphic = true,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);

  // Auto-dismiss after duration
  useEffect(() => {
    if (duration > 0) {
      const startTime = Date.now();
      const endTime = startTime + duration;

      // Create progress tracker animation
      const updateProgress = () => {
        const now = Date.now();
        const remaining = Math.max(0, endTime - now);
        const percentage = (remaining / duration) * 100;
        setProgress(percentage);

        if (percentage > 0) {
          requestAnimationFrame(updateProgress);
        } else {
          setIsVisible(false);
          if (onDismiss) onDismiss();
        }
      };

      const animationId = requestAnimationFrame(updateProgress);
      return () => cancelAnimationFrame(animationId);
    }
  }, [duration, onDismiss]);

  // Define variant-specific styles and icons
  const variantStyles: Record<
    ToastVariant,
    {
      containerClass: string;
      iconClass: string;
      iconComponent: React.ReactNode;
      progressBarClass: string;
      glowClass: string;
    }
  > = {
    info: {
      containerClass: glassmorphic
        ? "border-primary/20 text-white-medium"
        : "bg-primary-dark/90 border-primary/20 text-white-medium",
      iconClass: "text-primary",
      iconComponent: <Info size={20} />,
      progressBarClass: "bg-primary",
      glowClass: "glow-primary-subtle",
    },
    success: {
      containerClass: glassmorphic
        ? "border-green-500/20 text-white-medium"
        : "bg-green-900/90 border-green-500/20 text-white-medium",
      iconClass: "text-green-400",
      iconComponent: <CheckCircle size={20} />,
      progressBarClass: "bg-green-400",
      glowClass: "shadow-[0_0_15px_rgba(74,222,128,0.3)]",
    },
    warning: {
      containerClass: glassmorphic
        ? "border-yellow-500/20 text-white-medium"
        : "bg-yellow-900/90 border-yellow-500/20 text-white-medium",
      iconClass: "text-yellow-400",
      iconComponent: <AlertTriangle size={20} />,
      progressBarClass: "bg-yellow-400",
      glowClass: "shadow-[0_0_15px_rgba(250,204,21,0.3)]",
    },
    error: {
      containerClass: glassmorphic
        ? "border-red-500/20 text-white-medium"
        : "bg-red-900/90 border-red-500/20 text-white-medium",
      iconClass: "text-red-400",
      iconComponent: <AlertCircle size={20} />,
      progressBarClass: "bg-red-400",
      glowClass: "shadow-[0_0_15px_rgba(248,113,113,0.3)]",
    },
  };

  const {
    containerClass,
    iconClass,
    iconComponent,
    progressBarClass,
    glowClass,
  } = variantStyles[variant];

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  const baseClasses = `relative overflow-hidden flex items-start p-4 rounded-xl border ${containerClass} ${className}`;
  const glassmorphicClasses = glassmorphic ? "backdrop-blur-md bg-white/5" : "";
  const shadowClasses = glassmorphic ? glowClass : "shadow-lg";

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={`${baseClasses} ${glassmorphicClasses} ${shadowClasses}`}
          role="alert"
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex-shrink-0 mr-3">
            <div className={`${iconClass}`}>{icon || iconComponent}</div>
          </div>
          <div className="flex-1">
            {title && <h4 className="font-medium text-white mb-1">{title}</h4>}
            <div className="text-sm">{message}</div>
          </div>
          <button
            type="button"
            className="flex-shrink-0 ml-3 text-white-medium hover:text-white transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 rounded-full"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>

          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 h-0.5 w-full bg-white/10">
            <div
              className={`h-full ${progressBarClass} transition-all ease-linear`}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Glassmorphic top highlight */}
          {glassmorphic && (
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Toast utility for global use
export const toast = {
  activeToasts: [] as any[],
  listeners: [] as ((toasts: any[]) => void)[],

  subscribe(listener: (toasts: any[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  },

  notify() {
    this.listeners.forEach((l) => l(this.activeToasts));
  },

  show(variant: ToastVariant, title: string, message: string) {
    const id = Math.random().toString(36).substring(2, 9);
    this.activeToasts = [...this.activeToasts, { id, variant, title, message }];
    this.notify();
    return id;
  },

  dismiss(id: string) {
    this.activeToasts = this.activeToasts.filter((t) => t.id !== id);
    this.notify();
  },

  success(title: string, message: string) {
    return this.show("success", title, message);
  },
  error(title: string, message: string) {
    return this.show("error", title, message);
  },
  info(title: string, message: string) {
    return this.show("info", title, message);
  },
  warning(title: string, message: string) {
    return this.show("warning", title, message);
  },
};

// Toast container to manage multiple toasts
export const ToastContainer: React.FC<{
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  className?: string;
  children?: React.ReactNode;
}> = ({ position = "top-right", className = "", children }) => {
  const [toasts, setToasts] = useState<any[]>([]);

  useEffect(() => {
    return toast.subscribe((newToasts) => {
      setToasts(newToasts);
    });
  }, []);

  const positionClasses = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
  };

  return (
    <div
      className={`fixed z-[9999] flex flex-col gap-3 ${positionClasses[position]} ${className}`}
    >
      {toasts.map((t) => (
        <Toast
          key={t.id}
          variant={t.variant}
          title={t.title}
          message={t.message}
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))}
      {children}
    </div>
  );
};

export default Toast;
